import React, { useEffect, useState, useRef } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PlaceIcon from "@mui/icons-material/Place";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Grid,
  List,
  ListItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import { phaseTexts } from "../../utils/enumConverter";
import NAV from "../../utils/nav.const";
import ImageSizingContainer from "../common/ImageSizingContainer";
import theme from "../../styles/styleTheme";
import { useStages } from "../../hooks/tenants/useStages";
import { useEventsByIds } from "../../hooks/events/useEventsByids";
import LoadingAnimation from "../common/LoadingAnimation";
import { useTranslation } from "react-i18next";
import ApiFetch from "../../services/api.fetch";
import { URLS } from "../../utils/url";

const ProfileCandidatures = () => {
  const { user } = useUserAuth();
  const { t } = useTranslation();

  const { getData: getStages, data: stages, getStageById } = useStages();
  const { getDataByIds: getEventsByIds, data: events } = useEventsByIds();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [properties, setProperties] = useState<any[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const navigate = useNavigate();

  const [isAtBottom, setIsAtBottom] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getStages();
    getEventsByIds(user?.tenant?.attendingEvents);
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const propertyIds = user?.tenant?.candidatures
          .sort(
            (a: any, b: any) =>
              getStageById(b.stageId)?.orderNr -
              getStageById(a.stageId)?.orderNr
          )
          .map((property: any) => property?.propertyId);
        const response = await ApiFetch.httpPost(
          URLS.post.property.getPropertiesByIds,
          propertyIds
        );

        setProperties(response.filter((x: any) => x != null));
        setLoadingProperties(false);
      } catch (error) {
        setLoadingProperties(false);
        throw new Error("Something went wrong");
      }
    };

    if (user && stages && events) {
      if (user?.tenant?.candidatures) {
        fetchProperties();
      }
    }
  }, [user, stages, events]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const isBottom = scrollTop + clientHeight >= scrollHeight - 1;
      setIsAtBottom(isBottom);
    };

    container.addEventListener("scroll", handleScroll);

    // Initial check
    handleScroll();

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [properties]);

  const getCandidatureByPropertyId = (propertyId: number) => {
    return user?.tenant?.candidatures.find(
      (candidature: any) => candidature?.propertyId === propertyId
    );
  };

  useEffect(() => {
    document.title = `${t("profile.applications")} - GoHome.rent`;
  }, []);
  
  return (
    <Grid
      container
      item
      padding={isSmallScreen ? 1 : 3}
      xs={12}
      display={"flex"}
      alignContent={"flex-start"}
      sx={{
        position: "relative",
        ...(!isAtBottom && {
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "150px",
            pointerEvents: "none",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1))"
                : "linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))",
          },
        }),
      }}
    >
      <Grid item xs={12}>
        <Box display={"flex"} justifyContent={"flex-start"} gap={1}>
          <Box
            display={"flex"}
            alignContent={"center"}
            gap={1}
            alignItems={"center"}
          >
            <Box>
              <PendingActionsIcon color="primary" />
            </Box>

            <Typography variant="h3" fontWeight={"bold"}>
              {`${properties.length} ${t("profile.applications")}`}
            </Typography>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box
          ref={containerRef}
          sx={{
            height: "700px",
            overflowY: "auto",
          }}
        >
          <List disablePadding>
            {loadingProperties && (
              <Box
                width={"100%"}
                height={400}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <LoadingAnimation />
              </Box>
            )}
            {!loadingProperties && properties?.length === 0 && (
              <Box
                width={"100%"}
                height={400}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Typography variant="body1" color="gray">
                  {t("applications.none")}
                </Typography>
              </Box>
            )}
            {properties?.map((property, index) => {
              const candidature = getCandidatureByPropertyId(property?.id);

              return (
                <ListItem
                  sx={{
                    padding: 1,
                    paddingTop: 1.5,
                    paddingBottom: 1.5,
                    borderTop: index !== 0 ? "1px solid #eee" : null,
                    color: "#000",
                  }}
                  dense
                  divider={index !== properties.length - 1}
                  key={property?.id}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3} lg={3}>
                      <ImageSizingContainer
                        source={
                          property?.propertyMedia?.propertyImages.find(
                            (image: any) => image?.imageOrderPosition === 1
                          )?.link || require("../../assets/images/no-image.jpg")
                        }
                        alt="Property"
                        sx={{
                          borderRadius: "16px",
                          width: "100%",
                          height: "14em",
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm container>
                      <Grid item xs container direction="column" spacing={2}>
                        <Grid item xs>
                          {isSmallScreen ? (
                            <Box
                              display={"flex"}
                              alignItems={"flex-center"}
                              justifyContent={"flex-start"}
                              flexDirection={"column"}
                              gap={1}
                              marginBottom={2}
                            >
                              <Typography
                                fontWeight={500}
                                variant="body1"
                                dangerouslySetInnerHTML={{
                                  __html: phaseTexts(
                                    getStageById(candidature?.stageId)?.name,
                                    t
                                  )?.phase,
                                }}
                              />
                              <Box
                                display={"flex"}
                                justifyContent={"flex-start"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <PlaceIcon
                                  sx={{
                                    color: "grey",
                                  }}
                                />
                                <Typography color={"grey"} variant="body2">
                                  {property?.title}
                                </Typography>
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              display={"flex"}
                              alignItems={"center"}
                              justifyContent={"flex-start"}
                              gap={1}
                            >
                              <Typography
                                fontWeight={500}
                                variant="body1"
                                dangerouslySetInnerHTML={{
                                  __html: phaseTexts(
                                    getStageById(candidature?.stageId)?.name,
                                    t
                                  )?.phase,
                                }}
                              ></Typography>
                              <Box
                                sx={{
                                  height: "1.25rem",
                                  width: "2px",
                                  transform: "rotate(30deg)",
                                  backgroundColor: theme.palette.primary.main,
                                }}
                              />
                              <Typography variant="body2">
                                {property?.title}
                              </Typography>
                            </Box>
                          )}

                          {candidature?.rentingDate && (
                            <Typography variant="body2">{`${t(
                              "applications.available_from"
                            )} ${candidature?.rentingDate}`}</Typography>
                          )}
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            alignContent={"center"}
                            gap={1}
                          >
                            <Box
                              marginBottom={1}
                              display={"flex"}
                              gap={1}
                              alignItems={"center"}
                              flexDirection={isSmallScreen ? "column" : "row"}
                            ></Box>
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              dangerouslySetInnerHTML={{
                                __html: phaseTexts(
                                  getStageById(candidature?.stageId)?.name,
                                  t
                                )?.description,
                              }}
                            />
                          </Box>
                          {property?.participatingEvent &&
                            moment(
                              property?.participatingEvent?.parent?.endDateTime
                            ).isAfter(moment().subtract(3, "hours")) && (
                              <Chip
                                size={isSmallScreen ? "small" : "medium"}
                                sx={{
                                  background: theme.palette.primary.light,
                                  color: theme.palette.primary.main,
                                  marginTop: "1rem",
                                }}
                                icon={<CalendarMonthIcon color="primary" />}
                                label={
                                  <Typography variant="body2">
                                    {moment(
                                      property?.participatingEvent?.parent
                                        ?.startDateTime
                                    ).format("DD-MM-YYYY")}{" "}
                                    {" | "}
                                    {moment
                                      .utc(
                                        property?.participatingEvent?.parent
                                          ?.startDateTime
                                      )
                                      .format("HH:mm")}
                                    {" - "}
                                    {moment
                                      .utc(
                                        property?.participatingEvent?.parent
                                          ?.endDateTime
                                      )
                                      .format("HH:mm")}
                                  </Typography>
                                }
                              />
                            )}
                        </Grid>
                        <Grid item>
                          <Box
                            display={"flex"}
                            justifyContent={
                              isSmallScreen ? "center" : "flex-start"
                            }
                          >
                            <ButtonGroup
                              variant="text"
                              aria-label="Basic button group"
                            >
                              <Button
                                size="small"
                                color="error"
                                onClick={() => {
                                  navigate(
                                    `/${NAV.PROPERTY_DETAILS}/${property?.urlSlug}`
                                  );
                                }}
                                sx={{
                                  paddingRight: "1rem",
                                }}
                                endIcon={<OpenInNewIcon />}
                              >
                                {t("profile.view_property")}
                              </Button>
                              {getStageById(candidature?.stageId)?.name ===
                                "ScreeningInvited" && (
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    navigate(`/screening/${candidature?.id}`);
                                  }}
                                  sx={{
                                    paddingLeft: "1rem",
                                  }}
                                  endIcon={<OpenInNewIcon />}
                                >
                                  {["Screening", "SharedCandidates"].includes(
                                    getStageById(candidature?.stageId)?.name
                                  )
                                    ? t("profile.view_screening")
                                    : t("profile.to_screening")}
                                </Button>
                              )}
                            </ButtonGroup>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ProfileCandidatures;
